import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Network, X, Filter, Maximize2, Play, Pause, Clock } from "lucide-react";
import { API, verticalLabel, SCOPE_CONFIG, SCOPE_ORDER, VERTICALS } from "./kbConstants";

const VCOLOR = {
  haven: "#0d9488", grant: "#f59e0b", licensing: "#6366f1", brand: "#ec4899",
  retail: "#22c55e", pharma: "#06b6d4", pe: "#8b5cf6", general: "#64748b",
};
const W = 960, H = 680, CX = W / 2, CY = H / 2;
const SINCE_OPTS = [
  { value: "all", label: "Any time" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

function layout(nodes) {
  const entries = nodes.filter((n) => n.type === "entry");
  const tags = nodes.filter((n) => n.type === "tag");
  const byV = {};
  entries.forEach((n) => { (byV[n.vertical] = byV[n.vertical] || []).push(n); });
  const verticals = Object.keys(byV);
  const pos = {};
  const clusterR = Math.min(W, H) * 0.30;
  verticals.forEach((v, vi) => {
    const ang = (vi / Math.max(1, verticals.length)) * Math.PI * 2 - Math.PI / 2;
    const ccx = CX + Math.cos(ang) * clusterR * (verticals.length > 1 ? 1 : 0);
    const ccy = CY + Math.sin(ang) * clusterR * (verticals.length > 1 ? 1 : 0);
    const list = byV[v];
    const rr = 30 + list.length * 9;
    list.forEach((n, i) => {
      const a2 = (i / Math.max(1, list.length)) * Math.PI * 2;
      pos[n.id] = { x: ccx + Math.cos(a2) * (list.length === 1 ? 0 : rr),
                    y: ccy + Math.sin(a2) * (list.length === 1 ? 0 : rr) };
    });
  });
  const tagR = Math.min(W, H) * 0.46;
  tags.forEach((t, i) => {
    const a = (i / Math.max(1, tags.length)) * Math.PI * 2;
    pos[t.id] = { x: CX + Math.cos(a) * tagR, y: CY + Math.sin(a) * tagR };
  });
  return pos;
}

export default function KnowledgeGraph() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [q, setQ] = useState("");
  const [entry, setEntry] = useState(null);
  const [entryLoading, setEntryLoading] = useState(false);
  const [tags, setTags] = useState([]);

  const [fScope, setFScope] = useState("all");
  const [fVertical, setFVertical] = useState("all");
  const [fTag, setFTag] = useState("all");
  const [fSince, setFSince] = useState("all");
  const [cursor, setCursor] = useState(null);
  const [playing, setPlaying] = useState(false);
  const playRef = useRef(null);

  const times = useMemo(
    () => (data?.nodes || []).filter((n) => n.type === "entry" && n.created_at)
      .map((n) => new Date(n.created_at).getTime()).sort((a, b) => a - b),
    [data]);
  const minT = times[0] || 0;
  const maxT = times[times.length - 1] || 0;
  useEffect(() => { setCursor(maxT || null); }, [maxT]);

  useEffect(() => {
    if (!playing) return undefined;
    if (!minT || !maxT || minT === maxT) { setPlaying(false); return undefined; }
    const steps = 40, span = maxT - minT;
    let i = 0; setCursor(minT);
    playRef.current = setInterval(() => {
      i += 1; setCursor(minT + (span * i) / steps);
      if (i >= steps) { clearInterval(playRef.current); setPlaying(false); }
    }, 140);
    return () => clearInterval(playRef.current);
  }, [playing]);  // eslint-disable-line react-hooks/exhaustive-deps

  const cur = cursor ?? maxT;

  const runSearch = (term) => {
    setQ(term);
    if (!term.trim() || !data) { setFocusId(null); setSel(null); return; }
    const t = term.trim().toLowerCase();
    const m = data.nodes.find((n) => n.type === "entry" && (n.label || "").toLowerCase().includes(t));
    if (m) {
      setSel(m.id); setFocusId(m.id);
      if (m.created_at) {
        const mt = new Date(m.created_at).getTime();
        if (mt > cur) setCursor(mt);
      }
    } else { setFocusId(null); setSel(null); }
  };
  const visible = useMemo(() => {
    const set = new Set();
    (data?.nodes || []).forEach((n) => {
      if (n.type === "entry" && (!n.created_at || new Date(n.created_at).getTime() <= cur)) set.add(n.id);
    });
    (data?.edges || []).forEach((e) => {
      if (e.type === "tag" && set.has(e.source)) set.add(e.target);
    });
    return set;
  }, [data, cur]);

  const loadGraph = useCallback(async () => {
    setLoading(true); setSel(null);
    try {
      const params = {};
      if (fScope !== "all") params.scope = fScope;
      if (fVertical !== "all") params.vertical = fVertical;
      if (fTag !== "all") params.tag = fTag;
      if (fSince !== "all") {
        const d = new Date(); d.setDate(d.getDate() - parseInt(fSince, 10));
        params.since = d.toISOString();
      }
      const { data } = await axios.get(`${API}/kb/graph`, { params });
      setData(data);
    } catch (e) { toast.error("Could not load graph"); }
    finally { setLoading(false); }
  }, [fScope, fVertical, fTag, fSince]);

  useEffect(() => { loadGraph(); }, [loadGraph]);
  useEffect(() => {
    axios.get(`${API}/kb/tags`).then(({ data }) => setTags(data.tags || [])).catch(() => {});
  }, []);

  const openEntry = async (id) => {
    setEntryLoading(true);
    try {
      const { data } = await axios.get(`${API}/kb/entries/${id}`);
      setEntry(data);
    } catch (e) { toast.error("Could not open entry"); }
    finally { setEntryLoading(false); }
  };

  const pos = useMemo(() => (data ? layout(data.nodes) : {}), [data]);
  const nodeMap = useMemo(() => Object.fromEntries((data?.nodes || []).map((n) => [n.id, n])), [data]);

  const filters = (
    <div className="flex flex-wrap items-center gap-2" data-testid="kb-graph-filters">
      <div className="relative">
        <svg className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
        <input
          value={q}
          onChange={(e) => runSearch(e.target.value)}
          placeholder="Find a node…"
          className="h-8 pl-7 pr-2 text-xs rounded-md border border-[var(--qn-border)] bg-[var(--qn-card)] w-40 focus:outline-none focus:ring-1 focus:ring-[var(--qn-primary)]"
          data-testid="kb-graph-search"
        />
      </div>
      <Filter className="w-4 h-4 text-slate-400" />
      <GraphFilter value={fScope} onChange={setFScope} testid="kb-graph-filter-scope"
        options={[{ value: "all", label: "All scopes" }, ...SCOPE_ORDER.map((s) => ({ value: s, label: SCOPE_CONFIG[s].label }))]} />
      <GraphFilter value={fVertical} onChange={setFVertical} testid="kb-graph-filter-vertical"
        options={[{ value: "all", label: "All verticals" }, ...VERTICALS]} />
      <GraphFilter value={fTag} onChange={setFTag} testid="kb-graph-filter-tag"
        options={[{ value: "all", label: "All tags" }, ...tags.map((t) => ({ value: t, label: t }))]} />
      <GraphFilter value={fSince} onChange={setFSince} testid="kb-graph-filter-since" options={SINCE_OPTS} />
    </div>
  );

  return (
    <div className="space-y-3" data-testid="kb-graph">
      {filters}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : !data || data.nodes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="kb-graph-empty">
          <Network className="w-12 h-12 text-slate-300 mb-3" />
          <div className="font-semibold text-[var(--qn-foreground)]">Nothing to map for these filters</div>
          <p className="text-sm text-[var(--qn-foreground-muted)] max-w-sm mt-1">Adjust the filters or add tagged knowledge entries.</p>
        </div>
      ) : (
        <Card className="border-[var(--qn-border)]">
          <CardContent className="p-2 relative">
            {times.length > 1 && (
              <div className="flex items-center gap-3 px-2 pt-1 pb-2" data-testid="kb-graph-timeline">
                <button
                  onClick={() => setPlaying((p) => !p)}
                  className="w-7 h-7 rounded-full bg-[var(--qn-primary)] text-white flex items-center justify-center shrink-0"
                  data-testid="kb-graph-play"
                  title={playing ? "Pause" : "Watch the graph grow"}
                >
                  {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <input
                  type="range" min={minT} max={maxT} value={cur}
                  onChange={(e) => { setPlaying(false); setCursor(Number(e.target.value)); }}
                  className="flex-1 accent-[var(--qn-primary)]"
                  data-testid="kb-graph-slider"
                />
                <span className="text-[11px] text-slate-500 inline-flex items-center gap-1 shrink-0 w-24 justify-end">
                  <Clock className="w-3 h-3" />
                  {new Date(cur).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
              </div>
            )}
            <div className="absolute bottom-3 left-3 z-10 flex flex-wrap gap-2 pointer-events-none">
              {[...new Set(data.nodes.filter((n) => n.type === "entry").map((n) => n.vertical))].map((v) => (
                <span key={v} className="text-[10px] inline-flex items-center gap-1 text-slate-500 bg-[var(--qn-card)]/70 rounded px-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: VCOLOR[v] || VCOLOR.general }} />{verticalLabel(v)}
                </span>
              ))}
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 620 }} data-testid="kb-graph-svg">
              <g transform={`translate(${focusId && pos[focusId] ? CX - pos[focusId].x : 0},${focusId && pos[focusId] ? CY - pos[focusId].y : 0})`}
                 style={{ transition: "transform 0.5s ease" }}>
              {data.edges.map((e, i) => {
                const a = pos[e.source], b = pos[e.target];
                if (!a || !b || !visible.has(e.source) || !visible.has(e.target)) return null;
                const isTag = e.type === "tag";
                return (
                  <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke={isTag ? "#cbd5e1" : "#5eead4"}
                    strokeWidth={isTag ? 0.8 : 0.6 + e.weight * 2}
                    strokeDasharray={isTag ? "3 3" : "0"}
                    strokeOpacity={sel ? (e.source === sel || e.target === sel ? 0.9 : 0.1) : 0.5} />
                );
              })}
              {data.nodes.map((n) => {
                const p = pos[n.id];
                if (!p || !visible.has(n.id)) return null;
                const isTag = n.type === "tag";
                const color = isTag ? "#94a3b8" : (VCOLOR[n.vertical] || VCOLOR.general);
                const active = sel === n.id;
                return (
                  <g key={n.id} transform={`translate(${p.x},${p.y})`} style={{ cursor: "pointer" }}
                    onClick={() => setSel(active ? null : n.id)}
                    onDoubleClick={() => !isTag && openEntry(n.id)}
                    data-testid={`kb-graph-node-${n.id}`}>
                    <circle r={isTag ? 5 : n.size} fill={color}
                      fillOpacity={sel && !active ? 0.35 : 0.9}
                      stroke={active ? "#0f172a" : "#fff"} strokeWidth={active ? 2 : 1} />
                    {(!isTag || active) && (
                      <text x={0} y={(isTag ? 5 : n.size) + 10} textAnchor="middle"
                        fontSize={isTag ? 8 : 9} fill="#475569" style={{ pointerEvents: "none" }}>
                        {(n.label || "").slice(0, 22)}
                      </text>
                    )}
                  </g>
                );
              })}
              </g>
            </svg>

            {sel && nodeMap[sel] && nodeMap[sel].type === "entry" && (
              <div className="absolute bottom-3 right-3 w-72 bg-[var(--qn-card)] border border-[var(--qn-border)] rounded-lg p-3 shadow-lg" data-testid="kb-graph-detail">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-sm text-[var(--qn-foreground)]">{nodeMap[sel].label}</div>
                  <button onClick={() => setSel(null)}><X className="w-4 h-4 text-slate-400" /></button>
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">{verticalLabel(nodeMap[sel].vertical)} · {nodeMap[sel].scope}</div>
                <p className="text-xs text-[var(--qn-foreground-muted)] mt-1.5 line-clamp-3">{nodeMap[sel].summary}</p>
                <button onClick={() => openEntry(sel)} disabled={entryLoading}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[var(--qn-primary)] hover:underline"
                  data-testid="kb-graph-open-entry">
                  {entryLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Maximize2 className="w-3 h-3" />} Open full entry
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!entry} onOpenChange={(o) => !o && setEntry(null)}>
        <DialogContent className="max-w-xl" data-testid="kb-graph-entry-dialog">
          {entry && (
            <>
              <DialogHeader>
                <DialogTitle>{entry.title}</DialogTitle>
                <DialogDescription>Knowledge entry details</DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap items-center gap-2 -mt-1">
                <Badge variant="outline" className="text-[10px]">{verticalLabel(entry.metadata?.vertical)}</Badge>
                <Badge variant="outline" className="text-[10px]">{entry.scope}</Badge>
                <Badge variant="outline" className="text-[10px]">{entry.content_type}</Badge>
                {typeof entry.metadata?.confidence === "number" && (
                  <span className="text-[10px] text-slate-500">{Math.round(entry.metadata.confidence * 100)}% confidence</span>
                )}
              </div>
              <div className="text-sm text-[var(--qn-foreground)] whitespace-pre-wrap max-h-[50vh] overflow-y-auto">{entry.body}</div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(entry.metadata?.tags || []).map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GraphFilter({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-auto min-w-[130px] h-8 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}
